import React, { useEffect, useState } from 'react';

import { Button, LoadingButton, Modal } from '@common/index';
import { Stack, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, State, flowActions, FlowSaveAsTemplateModal, FlowModals } from '@store/index';
import { useTheme } from '../../../styles/theme';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Label } from '@common/Label';
import { TemplatesCreateArgs, templatesService, TemplateType } from '@requests/templates';
import { DocumentScanner } from '@mui/icons-material';

type TemplateState = {
  name: string;
  description: string;
};

/**
 * @IMPORTANT
 * For each new trigger type added, we need to update this mapping to support
 * saving workflows as templates with that trigger node. Otherwise the request
 * to save as template will throw an error.
 */

export const TriggerCommandIdMap = {
  'http.request': TemplateType.REQUEST,
  'call.initiated': TemplateType.CALL_CONTROL,
  'workflow.module': TemplateType.MODULE,
  'message.received': TemplateType.MESSAGING,
};

const initialTemplate = {
  name: '',
  description: '',
};

export const SaveAsTemplate: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();

  const [template, setTemplate] = useState<TemplateState>(initialTemplate);

  const modal = useSelector((state: State) => state.flow.modal) as FlowSaveAsTemplateModal;

  const isOpen = modal !== null && modal.type === FlowModals.SaveAsTemplate;
  const modalData = modal?.data;

  const { mutateAsync: saveTemplate, isPending: loadingWhileCreation } = useMutation({
    mutationFn: templatesService.create,
    onSuccess: (data) => {
      if (!data) return;
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      enqueueSnackbar('Workflow saved as template successfully!', { variant: 'success', autoHideDuration: 3000 });
      closeModal();
      resetTemplate();
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Error while saving template!', { variant: 'error', autoHideDuration: 3000 });
      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 3000,
      });
    },
  });

  const isLoading = loadingWhileCreation;

  const isNameError = Boolean(
    (template.name.length < 3 || template.name.length > 64 || template.name.length === 0) && template.name
  );

  useEffect(() => {
    const flowName = modalData?.name;
    if (flowName) {
      setTemplate((prev) => ({ ...prev, name: flowName }));
    }
  }, [modalData?.name]);

  const resetTemplate = () => {
    setTemplate(initialTemplate);
  };

  const closeModal = () => {
    dispatch(flowActions.setFlowModal(null));
  };

  const handleSave = async () => {
    //check if the template's data is not empty
    if (!modalData) return;
    // check if the template name
    if (!template.name) return;

    const data: TemplatesCreateArgs = {
      ...modalData,
      name: template.name,
      description: template.description,
    };

    enqueueSnackbar('Saving template...', { variant: 'info', autoHideDuration: 3000 });

    try {
      await saveTemplate(data);
    } catch {}
  };

  const handleTemplateValueChange = (prop: keyof TemplateState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setTemplate((prev) => ({ ...prev, [prop]: e.target.value }));
  };

  return (
    <Modal
      open={isOpen}
      setOpen={closeModal}
      header='Save as Template'
      background={theme.primary}
      icon={<DocumentScanner />}
      info='Create a new template from this workflow. Templates are accessible by all users in your organisation from any workspace.'
      body={
        <Stack spacing={3}>
          <Stack spacing='8px'>
            <Label title='Name' tooltip='A distinct name for your template.' />
            <TextField
              variant='outlined'
              value={template.name}
              onChange={handleTemplateValueChange('name')}
              placeholder='Name'
              fullWidth
              helperText={
                isNameError
                  ? `Must be ${template.name.length < 3 ? 'at least 3 characters' : 'less than 64 characters'}`
                  : undefined
              }
              required
            />
          </Stack>
          <Stack spacing='8px'>
            <Label
              title='Description (Optional)'
              tooltip='A short description of the template. This will help team members understand the purpose of this template.'
            />
            <TextField
              variant='outlined'
              value={template.description}
              onChange={handleTemplateValueChange('description')}
              placeholder='Description of the template'
              fullWidth
              minRows={3}
              maxRows={12}
              multiline
            />
          </Stack>
        </Stack>
      }
      actions={
        <Stack spacing={1} direction='row'>
          <Button size='medium' onClick={closeModal}>
            Cancel
          </Button>
          <LoadingButton
            size='medium'
            customColor='primary'
            minWidth='75px'
            onClick={handleSave}
            disabled={isLoading}
            loading={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </LoadingButton>
        </Stack>
      }
    />
  );
};
